import React, { useState, useEffect } from 'react';
import * as mui from '@mui/material';
import auditService from '../../../../services/AuditService';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningIcon from '@mui/icons-material/Warning';
import GppBadIcon from '@mui/icons-material/GppBad';
import SecurityIcon from '@mui/icons-material/Security';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import InfoIcon from '@mui/icons-material/Info';

const RiskRater = ({ 
  app, 
  company, 
  appId, 
  companyId, 
  nextStep,
  prevStep,
  selectedRisk,
  selectRisk
}) => {
  // State for risks
  const [mappedRisks, setMappedRisks] = useState([]);
  const [unratedRisks, setUnratedRisks] = useState([]);
  const [filteredRisks, setFilteredRisks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRated, setFilterRated] = useState('all');
  
  // State for UI
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [savingRiskId, setSavingRiskId] = useState(null);
  
  // State for risk rating
  const [currentRisk, setCurrentRisk] = useState(null);
  const [riskRating, setRiskRating] = useState('');
  const [ratingRationale, setRatingRationale] = useState('');
  
  useEffect(() => {
    const loadRisks = async () => {
      setLoading(true);
      try {
        // Get risks mapped to this app
        const mappingResponse = await auditService.fetchRiskMappingbyApp(appId, companyId);
        const mappedRiskData = mappingResponse?.risklist_data || [];
        
        setMappedRisks(mappedRiskData);
        
        // Filter unrated risks
        const unrated = mappedRiskData.filter(risk => !risk.RATING || risk.RATING === 'Not Set');
        setUnratedRisks(unrated);
        setFilteredRisks(mappedRiskData);
        
        // If there's a selected risk from parent, set it as current
        if (selectedRisk) {
          setCurrentRisk(selectedRisk);
          setRiskRating(selectedRisk.RATING || '');
          setRatingRationale(selectedRisk.RATING_RATIONALE || '');
        } else if (unrated.length > 0) {
          // Otherwise select the first unrated risk
          setCurrentRisk(unrated[0]);
          setRiskRating(unrated[0].RATING || '');
          setRatingRationale(unrated[0].RATING_RATIONALE || '');
        }
        
      } catch (error) {
        console.error('Error loading risks:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadRisks();
  }, [appId, companyId, selectedRisk]);

  // Filter risks based on search term and filters
  useEffect(() => {
    let filtered = [...mappedRisks];
    
    // Apply search term
    if (searchTerm) {
      filtered = filtered.filter(
        risk => 
          risk.RISK_ID?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          risk.RISK_NAME?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          risk.RISK_DESCRIPTION?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Apply rating filter
    if (filterRated === 'rated') {
      filtered = filtered.filter(risk => risk.RATING && risk.RATING !== 'Not Set');
    } else if (filterRated === 'unrated') {
      filtered = filtered.filter(risk => !risk.RATING || risk.RATING === 'Not Set');
    }
    
    setFilteredRisks(filtered);
  }, [searchTerm, filterRated, mappedRisks]);

  // Handle saving risk rating
  const handleSaveRating = async () => {
    if (!currentRisk) return;
    
    setSavingRiskId(currentRisk.id);
    
    try {
      const updatedData = {
        RATING: riskRating,
        RATING_RATIONALE: ratingRationale,
        RISK_ID: currentRisk.id,
        APP_NAME: appId,
        COMPANY_ID: companyId
      };
      
      await auditService.updateRiskMappingbyMapID(appId, currentRisk.id, companyId, updatedData);
      
      // Update the risk in the list
      setMappedRisks(prev => 
        prev.map(risk => 
          risk.id === currentRisk.id 
            ? { ...risk, RATING: riskRating, RATING_RATIONALE: ratingRationale } 
            : risk
        )
      );
      
      // Update unrated risks list
      setUnratedRisks(prev => {
        if (riskRating && riskRating !== 'Not Set') {
          return prev.filter(risk => risk.id !== currentRisk.id);
        }
        return prev;
      });
      
      // Show success message
      setSuccessMessage(`Risk "${currentRisk.RISK_ID}" rating saved successfully`);
      setShowSuccessAlert(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccessAlert(false);
      }, 3000);
      
      // Move to next unrated risk if available
      const nextUnrated = unratedRisks.find(risk => risk.id !== currentRisk.id);
      if (nextUnrated) {
        setCurrentRisk(nextUnrated);
        setRiskRating(nextUnrated.RATING || '');
        setRatingRationale(nextUnrated.RATING_RATIONALE || '');
      }
      
    } catch (error) {
      console.error('Error saving risk rating:', error);
    } finally {
      setSavingRiskId(null);
    }
  };

  // Handle selecting a risk to rate
  const handleSelectRisk = (risk) => {
    setCurrentRisk(risk);
    
    // If parent component has a selectRisk function, call it
    if (selectRisk) {
      // Only pass the risk without triggering navigation when already in Assess section
      const urlParams = new URLSearchParams(window.location.search);
      const section = urlParams.get('section');
      
      if (section === 'assess') {
        // Just update the selected risk without navigation
        selectRisk(risk, { preventNavigation: true });
      } else {
        // Default behavior for other sections
        selectRisk(risk);
      }
    }
  };

  // Get risk rating badge color
  const getRatingColor = (rating) => {
    switch(rating) {
      case 'High': return '#d32f2f';
      case 'Medium': return '#ff9800';
      case 'Low': return '#2e7d32';
      default: return '#757575';
    }
  };

  // Get risk icon based on type
  const getRiskTypeIcon = (type) => {
    switch(type) {
      case 'IT': return <SecurityIcon fontSize="small" />;
      case 'Business Process': return <ErrorOutlineIcon fontSize="small" />;
      case 'Operational': return <WarningIcon fontSize="small" />;
      case 'Entity Level': return <GppBadIcon fontSize="small" />;
      default: return <ErrorOutlineIcon fontSize="small" />;
    }
  };

  // Rating options
  const ratingOptions = [
    { value: 'High', label: 'High', color: '#d32f2f' },
    { value: 'Medium', label: 'Medium', color: '#ff9800' },
    { value: 'Low', label: 'Low', color: '#2e7d32' }
  ];

  // Risk card component for the list
  const RiskListItem = ({ risk }) => (
    <mui.ListItem 
      disablePadding
      sx={{
        mb: 1,
        backgroundColor: currentRisk?.id === risk.id ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
        borderRadius: 1,
        '&:hover': {
          backgroundColor: currentRisk?.id === risk.id ? 'rgba(25, 118, 210, 0.12)' : 'rgba(0, 0, 0, 0.04)'
        }
      }}
    >
      <mui.ListItemButton 
        onClick={() => handleSelectRisk(risk)}
        sx={{ borderRadius: 1 }}
      >
        <mui.ListItemAvatar>
          <mui.Avatar 
            sx={{ 
              bgcolor: risk.RISK_TYPE === 'IT' ? 'primary.light' : 
                      risk.RISK_TYPE === 'Business Process' ? 'secondary.light' :
                      risk.RISK_TYPE === 'Operational' ? 'warning.light' : 'error.light',
              width: 36, 
              height: 36
            }}
          >
            {getRiskTypeIcon(risk.RISK_TYPE)}
          </mui.Avatar>
        </mui.ListItemAvatar>
        <mui.ListItemText
          primary={
            <mui.Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <mui.Typography variant="subtitle2" noWrap sx={{ maxWidth: '130px' }}>
                {risk.RISK_ID}
              </mui.Typography>
              {risk.RATING && risk.RATING !== 'Not Set' ? (
                <mui.Chip 
                  size="small"
                  label={risk.RATING}
                  sx={{ 
                    backgroundColor: `${getRatingColor(risk.RATING)}15`,
                    color: getRatingColor(risk.RATING),
                    fontWeight: 'bold',
                    height: 24
                  }}
                />
              ) : (
                <mui.Chip 
                  size="small"
                  label="Not Rated"
                  sx={{ 
                    backgroundColor: '#f5f5f5',
                    color: '#757575',
                    height: 24
                  }}
                />
              )}
            </mui.Box>
          }
          secondary={
            <mui.Typography 
              variant="body2" 
              color="textSecondary" 
              sx={{
                display: '-webkit-box',
                overflow: 'hidden',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 1
              }}
            >
              {risk.RISK_NAME}
            </mui.Typography>
          }
        />
      </mui.ListItemButton>
    </mui.ListItem>
  );

  return (
    <mui.Box>
      {/* Success notification */}
      <mui.Snackbar
        open={showSuccessAlert}
        autoHideDuration={3000}
        onClose={() => setShowSuccessAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <mui.Alert
          severity="success"
          variant="filled"
          icon={<CheckCircleIcon fontSize="inherit" />}
        >
          {successMessage}
        </mui.Alert>
      </mui.Snackbar>
      
      <mui.Grid container spacing={3}>
        {/* Left panel - Risk list */}
        <mui.Grid item xs={12} md={4}>
          <mui.Paper 
            elevation={1} 
            sx={{ 
              height: '100%', 
              p: 2, 
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <mui.Typography variant="h6" sx={{ mb: 2 }}>
              Identified Risks
              <mui.Chip 
                label={mappedRisks.length} 
                size="small" 
                sx={{ ml: 1 }} 
                color="primary"
              />
            </mui.Typography>
            
            {/* Statistics - Moved to top */}
            <mui.Box 
              sx={{ 
                mb: 2, 
                py: 1.5, 
                borderRadius: 1,
                bgcolor: 'background.default',
                display: 'flex',
                justifyContent: 'space-around',
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <mui.Box sx={{ textAlign: 'center' }}>
                <mui.Typography variant="caption" color="textSecondary">
                  Unrated
                </mui.Typography>
                <mui.Typography variant="h6" color="text.secondary">
                  {unratedRisks.length}
                </mui.Typography>
              </mui.Box>
              
              <mui.Box sx={{ textAlign: 'center' }}>
                <mui.Typography variant="caption" color="error">
                  High
                </mui.Typography>
                <mui.Typography variant="h6" color="error.main">
                  {mappedRisks.filter(r => r.RATING === 'High').length}
                </mui.Typography>
              </mui.Box>
              
              <mui.Box sx={{ textAlign: 'center' }}>
                <mui.Typography variant="caption" color="warning.dark">
                  Medium
                </mui.Typography>
                <mui.Typography variant="h6" color="warning.dark">
                  {mappedRisks.filter(r => r.RATING === 'Medium').length}
                </mui.Typography>
              </mui.Box>
              
              <mui.Box sx={{ textAlign: 'center' }}>
                <mui.Typography variant="caption" color="success">
                  Low
                </mui.Typography>
                <mui.Typography variant="h6" color="success.main">
                  {mappedRisks.filter(r => r.RATING === 'Low').length}
                </mui.Typography>
              </mui.Box>
            </mui.Box>
            
            {/* Search and filter */}
            <mui.Box sx={{ mb: 2 }}>
              <mui.TextField
                placeholder="Search risks..."
                variant="outlined"
                size="small"
                fullWidth
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <mui.InputAdornment position="start">
                      <SearchIcon color="action" />
                    </mui.InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <mui.InputAdornment position="end">
                      <mui.IconButton size="small" onClick={() => setSearchTerm('')}>
                        <mui.Icon fontSize="small">clear</mui.Icon>
                      </mui.IconButton>
                    </mui.InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
              />
              
              <mui.Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <mui.Chip
                  label={
                    <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <mui.Typography variant="body2">All Risks</mui.Typography>
                      <mui.Chip 
                        size="small" 
                        label={mappedRisks.length} 
                        sx={{ 
                          height: 20,
                          minWidth: 20,
                          '& .MuiChip-label': { px: 0.5 }
                        }}
                      />
                    </mui.Box>
                  }
                  onClick={() => setFilterRated('all')}
                  color={filterRated === 'all' ? 'primary' : 'default'}
                  variant={filterRated === 'all' ? 'filled' : 'outlined'}
                  sx={{ 
                    '& .MuiChip-label': { p: 1 },
                    backgroundColor: filterRated === 'all' ? 'primary.main' : 'transparent',
                    '&:hover': {
                      backgroundColor: filterRated === 'all' ? 'primary.dark' : 'action.hover'
                    }
                  }}
                />
                <mui.Chip
                  label={
                    <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <mui.Typography variant="body2">Rated</mui.Typography>
                      <mui.Chip 
                        size="small" 
                        label={mappedRisks.filter(r => r.RATING && r.RATING !== 'Not Set').length} 
                        sx={{ 
                          height: 20,
                          minWidth: 20,
                          '& .MuiChip-label': { px: 0.5 }
                        }}
                      />
                    </mui.Box>
                  }
                  onClick={() => setFilterRated('rated')}
                  color={filterRated === 'rated' ? 'primary' : 'default'}
                  variant={filterRated === 'rated' ? 'filled' : 'outlined'}
                  sx={{ 
                    '& .MuiChip-label': { p: 1 },
                    backgroundColor: filterRated === 'rated' ? 'primary.main' : 'transparent',
                    '&:hover': {
                      backgroundColor: filterRated === 'rated' ? 'primary.dark' : 'action.hover'
                    }
                  }}
                />
                <mui.Chip
                  label={
                    <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <mui.Typography variant="body2">Unrated</mui.Typography>
                      <mui.Chip 
                        size="small" 
                        label={unratedRisks.length} 
                        sx={{ 
                          height: 20,
                          minWidth: 20,
                          '& .MuiChip-label': { px: 0.5 }
                        }}
                      />
                    </mui.Box>
                  }
                  onClick={() => setFilterRated('unrated')}
                  color={filterRated === 'unrated' ? 'primary' : 'default'}
                  variant={filterRated === 'unrated' ? 'filled' : 'outlined'}
                  sx={{ 
                    '& .MuiChip-label': { p: 1 },
                    backgroundColor: filterRated === 'unrated' ? 'primary.main' : 'transparent',
                    '&:hover': {
                      backgroundColor: filterRated === 'unrated' ? 'primary.dark' : 'action.hover'
                    }
                  }}
                />
              </mui.Box>
            </mui.Box>
            
            {/* Risk list */}
            {loading ? (
              <mui.Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <mui.CircularProgress />
              </mui.Box>
            ) : filteredRisks.length === 0 ? (
              <mui.Box 
                sx={{ 
                  p: 3, 
                  textAlign: 'center',
                  backgroundColor: '#f9f9f9',
                  borderRadius: 2,
                  flexGrow: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}
              >
                <mui.Typography variant="subtitle1" color="textSecondary" gutterBottom>
                  No risks found
                </mui.Typography>
                <mui.Typography variant="body2" color="textSecondary">
                  {searchTerm || filterRated !== 'all' 
                    ? 'No risks match your search criteria. Try adjusting your filters.' 
                    : 'There are no risks identified yet. Go back to the Risk Identification step.'}
                </mui.Typography>
                {(!searchTerm && filterRated === 'all' && mappedRisks.length === 0) && (
                  <mui.Button 
                    variant="outlined" 
                    color="primary" 
                    sx={{ mt: 2, alignSelf: 'center' }}
                    onClick={prevStep}
                    startIcon={<ArrowBackIcon />}
                  >
                    Identify Risks
                  </mui.Button>
                )}
              </mui.Box>
            ) : (
              <mui.List sx={{ overflow: 'auto', flexGrow: 1 }}>
                {filteredRisks.map(risk => (
                  <RiskListItem key={risk.id} risk={risk} />
                ))}
              </mui.List>
            )}
          </mui.Paper>
        </mui.Grid>
        
        {/* Right panel - Risk rating form */}
        <mui.Grid item xs={12} md={8}>
          <mui.Paper 
            elevation={1} 
            sx={{ 
              height: '100%', 
              p: 3, 
              borderRadius: 2,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {loading ? (
              <mui.Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <mui.CircularProgress />
              </mui.Box>
            ) : !currentRisk ? (
              <mui.Box 
                sx={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  height: '100%',
                  p: 3
                }}
              >
                <ErrorOutlineIcon color="action" sx={{ fontSize: 64, mb: 2, opacity: 0.6 }} />
                <mui.Typography variant="h6" color="textSecondary" gutterBottom>
                  No Risk Selected
                </mui.Typography>
                <mui.Typography 
                  variant="body1" 
                  color="textSecondary" 
                  align="center"
                  sx={{ maxWidth: 500, mb: 3 }}
                >
                  Select a risk from the list on the left to rate its impact and likelihood,
                  or go back to identify relevant risks for the application.
                </mui.Typography>
                
                <mui.Button
                  variant="outlined"
                  color="primary"
                  onClick={prevStep}
                  startIcon={<ArrowBackIcon />}
                >
                  Back to Risk Identification
                </mui.Button>
              </mui.Box>
            ) : (
              <>
                {/* Risk details - Minimalist design */}
                <mui.Paper 
                  elevation={0}
                  sx={{ 
                    mb: 3,
                    borderRadius: '4px',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  {/* Risk header */}
                  <mui.Box 
                    sx={{ 
                      p: 2.5,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'background.paper'
                    }}
                  >
                    <mui.Grid container spacing={2} alignItems="center">
                      {/* Risk Icon */}
                      <mui.Grid item>
                        <mui.Avatar 
                          sx={{ 
                            bgcolor: `${getRatingColor(currentRisk.RATING)}10`,
                            color: getRatingColor(currentRisk.RATING),
                            width: 48, 
                            height: 48
                          }}
                        >
                          {getRiskTypeIcon(currentRisk.RISK_TYPE)}
                        </mui.Avatar>
                      </mui.Grid>
                      
                      {/* Risk ID and Name */}
                      <mui.Grid item xs>
                        <mui.Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <mui.Typography 
                            variant="caption" 
                            color="text.secondary"
                            sx={{ display: 'block', mb: 0.5 }}
                          >
                            {currentRisk.RISK_ID}
                          </mui.Typography>
                          <mui.Chip 
                            size="small" 
                            label={currentRisk.RISK_TYPE} 
                            variant="outlined"
                            sx={{ 
                              height: 20, 
                              fontSize: '0.7rem',
                              ml: 1
                            }}
                          />
                        </mui.Box>
                        <mui.Typography 
                          variant="subtitle1" 
                          sx={{ fontWeight: 500, lineHeight: 1.3 }}
                        >
                          {currentRisk.RISK_NAME}
                        </mui.Typography>
                      </mui.Grid>
                      
                      {/* Rating Badge */}
                      <mui.Grid item>
                        {currentRisk.RATING && currentRisk.RATING !== 'Not Set' ? (
                          <mui.Chip 
                            label={currentRisk.RATING} 
                            size="small"
                            sx={{ 
                              bgcolor: `${getRatingColor(currentRisk.RATING)}10`,
                              color: getRatingColor(currentRisk.RATING),
                              fontWeight: 'medium',
                              minWidth: 70,
                              border: '1px solid',
                              borderColor: `${getRatingColor(currentRisk.RATING)}30`
                            }}
                          />
                        ) : (
                          <mui.Chip 
                            label="Not Rated" 
                            size="small"
                            variant="outlined"
                            sx={{ 
                              color: 'text.secondary',
                              fontWeight: 'medium'
                            }}
                          />
                        )}
                      </mui.Grid>
                    </mui.Grid>
                  </mui.Box>
                  
                  {/* Risk Description - Only show if it exists */}
                  {currentRisk.RISK_DESCRIPTION && (
                    <mui.Box sx={{ px: 2.5, py: 2, bgcolor: 'grey.50' }}>
                      <mui.Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1 }}
                      >
                        Description
                      </mui.Typography>
                      <mui.Typography variant="body2" color="text.primary">
                        {currentRisk.RISK_DESCRIPTION}
                      </mui.Typography>
                    </mui.Box>
                  )}
                </mui.Paper>
                
                {/* Rating form */}
                <mui.Typography variant="h6" gutterBottom>
                  Risk Assessment
                </mui.Typography>
                
                <mui.Typography variant="body2" color="textSecondary" paragraph>
                  Assess the risk based on its potential impact and likelihood of occurrence.
                  Rate the risk as High, Medium, or Low and provide a rationale for your rating.
                </mui.Typography>
                
                {/* Rating buttons */}
                <mui.Box sx={{ mb: 3 }}>
                  <mui.Typography variant="subtitle2" gutterBottom>
                    Risk Rating:
                  </mui.Typography>
                  <mui.Box sx={{ display: 'flex', gap: 2 }}>
                    {ratingOptions.map(option => (
                      <mui.Button
                        key={option.value}
                        variant={riskRating === option.value ? "contained" : "outlined"}
                        onClick={() => setRiskRating(option.value)}
                        sx={{ 
                          minWidth: 100,
                          backgroundColor: riskRating === option.value ? option.color : 'transparent',
                          color: riskRating === option.value ? 'white' : option.color,
                          borderColor: option.color,
                          '&:hover': {
                            backgroundColor: riskRating === option.value 
                              ? option.color 
                              : `${option.color}20`,
                            borderColor: option.color
                          }
                        }}
                      >
                        {option.label}
                      </mui.Button>
                    ))}
                  </mui.Box>
                </mui.Box>
                
                {/* Rationale */}
                <mui.Typography variant="subtitle2" gutterBottom>
                  Rating Rationale:
                </mui.Typography>
                <mui.TextField
                  multiline
                  rows={4}
                  fullWidth
                  placeholder="Provide a rationale for your risk rating, including potential impact and likelihood considerations..."
                  value={ratingRationale}
                  onChange={(e) => setRatingRationale(e.target.value)}
                  sx={{ mb: 3 }}
                />
                
                <mui.Box sx={{ mt: 'auto' }}>
                  <mui.Divider sx={{ mb: 2 }} />
                  <mui.Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <mui.Button
                      variant="outlined"
                      onClick={prevStep}
                      startIcon={<ArrowBackIcon />}
                    >
                      Back
                    </mui.Button>
                    
                    <mui.Box sx={{ display: 'flex', gap: 2 }}>
                      <mui.Button
                        variant="contained"
                        color="primary"
                        onClick={handleSaveRating}
                        disabled={!riskRating || savingRiskId === currentRisk.id}
                        startIcon={savingRiskId === currentRisk.id ? <mui.CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                      >
                        {savingRiskId === currentRisk.id ? 'Saving...' : 'Save Rating'}
                      </mui.Button>
                      
                      <mui.Button
                        variant="contained"
                        color="primary"
                        onClick={nextStep}
                        disabled={unratedRisks.length > 0}
                        endIcon={<ArrowForwardIcon />}
                      >
                        Continue
                      </mui.Button>
                    </mui.Box>
                  </mui.Box>
                </mui.Box>
              </>
            )}
          </mui.Paper>
        </mui.Grid>
      </mui.Grid>
    </mui.Box>
  );
};

export default RiskRater; 